import { watch } from "@arcgis/core/core/reactiveUtils";
import { getAnyLayerGraphic, getPopupPlacement, zoomIntoCluster } from "@components/world-map/helpers/world-map-popup-helpers";
import { isClusterGraphic } from "@components/world-map/world-map-utils";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { WorldMapEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useEffect, useEffectEvent, useMemo, useRef, useState } from "react";

/**
 * Manages the marker popup lifecycle for the world map.
 * Handles click-to-open, pointer cursor updates, and popup repositioning during pan/zoom.
 */
export function useWorldMapPopups({
  viewRef,
  layerRef,
  isViewReady,
  entities,
  mapContext,
  isPopupEnabled,
  onMapClick
}) {
  const {
    handleErrorWithToast
  } = useErrorContext();
  const {
    trackEvent
  } = useTrackEvent();
  const [selectedMarkerPopup, setSelectedMarkerPopup] = useState(null);
  const isPointerHitTestInFlight = useRef(false);
  const shouldRunPointerHitTestAgain = useRef(false);
  const latestPointerScreenPoint = useRef(null);
  const pointerMoveFrameHandle = useRef(null);
  const markerClickHandle = useRef(null);
  const markerPointerMoveHandle = useRef(null);
  const extentWatchHandle = useRef(null);
  const entitiesById = useMemo(() => {
    return new Map(entities.map(entity => [entity.id, entity]));
  }, [entities]);

  /**
   * Handles map click and opens/closes marker popup.
   * When a cluster is clicked, zooms in to reveal individual markers.
   */
  const handleMapClick = useEffectEvent(async event => {
    try {
      event.stopPropagation();
      if (!viewRef.current) {
        return;
      }
      setSelectedMarkerPopup(null);
      const graphic = await getAnyLayerGraphic(viewRef.current, layerRef.current, event);

      // If click is not on a marker or cluster, trigger optional onMapClick callback and return early.
      if (!graphic) {
        onMapClick?.(event);
        return;
      }

      // When a cluster is clicked, zoom into it to reveal the individual markers.
      if (isClusterGraphic(graphic)) {
        trackEvent({
          name: WorldMapEvents.expandCluster,
          props: {
            clusterCount: Number(graphic.attributes?.cluster_count),
            mapContext
          }
        });
        await zoomIntoCluster(viewRef.current, graphic.layer, graphic);
        return;
      }
      const entityId = graphic.attributes?.id;
      const selectedEntity = entitiesById.get(String(entityId));
      if (!selectedEntity) {
        return;
      }
      const markerMapPoint = graphic.geometry?.type === "point" ? graphic.geometry : event.mapPoint;
      if (!markerMapPoint) {
        return;
      }
      const markerScreenPoint = viewRef.current.toScreen(markerMapPoint);
      if (!markerScreenPoint) {
        return;
      }
      const {
        placement,
        horizontalPlacement
      } = getPopupPlacement(markerScreenPoint, viewRef.current.width, viewRef.current.height);
      setSelectedMarkerPopup({
        entity: selectedEntity,
        mapPoint: markerMapPoint,
        x: markerScreenPoint.x,
        y: markerScreenPoint.y,
        placement,
        horizontalPlacement
      });

      // Maps without popup content never render a popup, so nothing is opened there.
      if (isPopupEnabled) {
        trackEvent({
          name: WorldMapEvents.openMarkerPopup,
          props: {
            mapContext
          }
        });
      }
    } catch (error) {
      handleErrorWithToast({
        id: `openMapMarkerPopup-${Date.now().toString()}`,
        title: "Failed to open map marker popup.",
        error
      });
      setSelectedMarkerPopup(null);
    }
  });

  /**
   * Updates cursor style while pointer moves over clickable markers or clusters.
   */
  const handleMapPointerMove = useEffectEvent(async () => {
    if (!viewRef.current?.container) {
      return;
    }
    if (!latestPointerScreenPoint.current || !isPopupEnabled) {
      viewRef.current.container.style.cursor = "default";
      return;
    }
    if (isPointerHitTestInFlight.current) {
      shouldRunPointerHitTestAgain.current = true;
      return;
    }
    isPointerHitTestInFlight.current = true;
    try {
      const graphic = await getAnyLayerGraphic(viewRef.current, layerRef.current, latestPointerScreenPoint.current);
      viewRef.current.container.style.cursor = graphic ? "pointer" : "default";
    } finally {
      isPointerHitTestInFlight.current = false;
      if (shouldRunPointerHitTestAgain.current) {
        shouldRunPointerHitTestAgain.current = false;
        void handleMapPointerMove();
      }
    }
  });

  /**
   * Throttles pointer hit-tests to one run per animation frame.
   */
  const schedulePointerCursorUpdate = useEffectEvent(() => {
    if (pointerMoveFrameHandle.current !== null) {
      return;
    }
    pointerMoveFrameHandle.current = window.requestAnimationFrame(() => {
      pointerMoveFrameHandle.current = null;
      void handleMapPointerMove();
    });
  });

  // Register map listeners when view is ready and clean them up on dependency change/unmount.
  useEffect(() => {
    if (!isViewReady || !viewRef.current) {
      return;
    }

    // Setup extent watcher for updating popup position during pan/zoom
    extentWatchHandle.current?.remove();
    extentWatchHandle.current = watch(() => viewRef.current?.extent, () => {
      setSelectedMarkerPopup(currentPopup => {
        if (!currentPopup || !viewRef.current) {
          return currentPopup;
        }
        const markerScreenPoint_0 = viewRef.current.toScreen(currentPopup.mapPoint);
        if (!markerScreenPoint_0) {
          return currentPopup;
        }
        const {
          placement: placement_0,
          horizontalPlacement: horizontalPlacement_0
        } = getPopupPlacement(markerScreenPoint_0, viewRef.current.width, viewRef.current.height);
        return {
          ...currentPopup,
          x: markerScreenPoint_0.x,
          y: markerScreenPoint_0.y,
          placement: placement_0,
          horizontalPlacement: horizontalPlacement_0
        };
      });
    });

    // Setup click handler
    markerClickHandle.current?.remove();
    markerClickHandle.current = viewRef.current.on("click", event_0 => {
      void handleMapClick(event_0);
    });

    // Setup pointer move handler
    markerPointerMoveHandle.current?.remove();
    markerPointerMoveHandle.current = viewRef.current.on("pointer-move", event_1 => {
      if (!isPopupEnabled) {
        return;
      }
      latestPointerScreenPoint.current = {
        x: event_1.x,
        y: event_1.y
      };
      schedulePointerCursorUpdate();
    });
    return () => {
      extentWatchHandle.current?.remove();
      markerClickHandle.current?.remove();
      markerPointerMoveHandle.current?.remove();
      if (pointerMoveFrameHandle.current !== null) {
        window.cancelAnimationFrame(pointerMoveFrameHandle.current);
      }
    };
  }, [isViewReady, isPopupEnabled, viewRef]);

  // Sync open popup entity with updated store data (e.g. if access level or features change while popup is open)
  useEffect(() => {
    setSelectedMarkerPopup(currentPopup_0 => {
      if (!currentPopup_0) {
        return null;
      }
      const updatedEntity = entitiesById.get(currentPopup_0.entity.id);

      // If the entity no longer exists (e.g. archived), close the popup.
      if (!updatedEntity) {
        return null;
      }

      // Update entity if its reference has changed in the store
      if (updatedEntity !== currentPopup_0.entity) {
        return {
          ...currentPopup_0,
          entity: updatedEntity
        };
      }
      return currentPopup_0;
    });
  }, [entitiesById]);
  return {
    selectedMarkerPopup
  };
}